// Data de ejemplo del módulo Frescura (demo front-end para feedback).
// Números y narrativa vienen del mock aprobado "Vida útil en la cadena".

export const STATUS_COLORS = {
  holgado: '#17C964',
  ajustado: '#F5A524',
  leve: '#EA580C',
  critico: '#BE123C',
  noVendido: '#D4D4D8',
} as const;

// Rampa cherry para los tramos (la vida se consume hacia el brand);
// el remanente va en verde éxito.
export const STAGE_COLORS = ['#FECDD3', '#FDA4AF', '#FB7185', '#BE123C', '#17C964'] as const;

export const COHORT = {
  chips: [
    { label: 'Especie', value: 'Cereza' },
    { label: 'Variedad', value: 'Todas (7)' },
    { label: 'Destino', value: 'China · marítimo' },
    { label: 'Semanas embalaje', value: '50–52' },
    { label: 'Vía', value: 'Marítimo' },
  ],
  methods: [
    {
      key: 'container',
      name: 'Devolución de contenedor',
      coverage: '97% del volumen · 9 exportadoras',
      enabled: true,
    },
    {
      key: 'sale',
      name: 'Venta real',
      coverage: '12% del volumen · 3 exportadoras — bajo el mínimo',
      enabled: false,
    },
  ],
  n: 9,
  note: 'La cohorte supera el mínimo de 5 exportadoras, así que se muestran los percentiles de industria. Tu volumen queda excluido del cálculo del benchmark.',
};

export const RULE = {
  consumedPct: '92%',
  consumedDays: '32,3',
  totalDays: '35,2',
  yourIndex: '0,92',
  industryIndex: '0,94',
  yourRemaining: '2,7 d',
  industryRemaining: '1,8 d',
  industryMarkPct: 94.86,
  pre: { days: '2,1 d', note: 'Cosecha → embalaje', sub: 'cobertura 62% · fuera del índice' },
  segments: [
    { key: 'Emb→Desp', days: '1,8', pct: 5.14, legend: 'Embalaje → despacho packing' },
    { key: 'Desp→Zarpe', days: '2,4', pct: 6.86, legend: 'Despacho → zarpe' },
    { key: 'Zarpe → Arribo', days: '24,5', pct: 70, legend: 'Zarpe → arribo' },
    { key: 'Arr→Desc', days: '3,6', pct: 10.29, legend: 'Arribo → desconsolidación' },
    { key: 'Remanente', days: '2,7', pct: 7.71, legend: 'Vida útil remanente' },
  ],
};

export const DISTRIBUTION = [
  { name: 'Holgado', range: 'índice ≤ 0,70', you: 8, industry: 6, color: STATUS_COLORS.holgado, dark: true },
  { name: 'Ajustado', range: '0,70 – 1,00', you: 61, industry: 52, color: STATUS_COLORS.ajustado, dark: false },
  { name: 'Vencido leve', range: '1,00 – 1,20', you: 22, industry: 29, color: STATUS_COLORS.leve, dark: true },
  { name: 'Vencido crítico', range: '> 1,20', you: 6, industry: 9, color: STATUS_COLORS.critico, dark: true },
  { name: 'No vendido', range: 'merma y destrucción', you: 3, industry: 4, color: STATUS_COLORS.noVendido, dark: false },
];

// Tracks de percentiles: posiciones en % del ancho del track.
export interface PercRow {
  name: string;
  tag?: { label: string; warn?: boolean };
  sub: string;
  iqrLeft: number;
  iqrWidth: number;
  median: number;
  threshold?: number;
  you: number;
  youLabel: string;
  gap: string; // "+0,04" | "−0,03" | "0,00"
}

export const DESTINATIONS: PercRow[] = [
  { name: 'China', tag: { label: 'marítimo', warn: true }, sub: '62% vol · 24,5 d tránsito', iqrLeft: 65.56, iqrWidth: 16.67, median: 72.22, threshold: 77.78, you: 68.89, youLabel: '0,92', gap: '−0,03' },
  { name: 'Europa', tag: { label: 'marítimo', warn: true }, sub: '9% vol · 23,1 d tránsito', iqrLeft: 53.33, iqrWidth: 16.67, median: 60, threshold: 77.78, you: 64.44, youLabel: '0,88', gap: '+0,04' },
  { name: 'Corea del Sur', tag: { label: 'marítimo', warn: true }, sub: '4% vol · 22,4 d tránsito', iqrLeft: 60, iqrWidth: 16.67, median: 66.67, threshold: 77.78, you: 62.22, youLabel: '0,86', gap: '−0,04' },
  { name: 'EE.UU. Este', tag: { label: 'marítimo', warn: true }, sub: '12% vol · 15,8 d tránsito', iqrLeft: 35.56, iqrWidth: 17.78, median: 42.22, threshold: 77.78, you: 45.56, youLabel: '0,71', gap: '+0,03' },
  { name: 'China', tag: { label: 'aéreo' }, sub: '8% vol · 3,2 d tránsito', iqrLeft: 8.89, iqrWidth: 15.56, median: 15.56, threshold: 77.78, you: 12.22, youLabel: '0,41', gap: '−0,03' },
  { name: 'EE.UU.', tag: { label: 'aéreo' }, sub: '5% vol · 2,8 d tránsito', iqrLeft: 5.56, iqrWidth: 13.33, median: 11.11, threshold: 77.78, you: 8.89, youLabel: '0,38', gap: '−0,02' },
];

export const DESTINATIONS_NOTE =
  'EE.UU. Este llega con 0,71 contra 0,92 de China: casi un tercio más de vida útil disponible al desconsolidar. Pero es tu única brecha negativa relevante en marítimo (+0,03), así que ahí sí hay algo que corregir en tu operación, no en la distancia. Ojo con el proxy: los free days y la práctica de desconsolidación cambian por mercado, así que el último tramo no es igual de comparable entre destinos.';

export const WEEK_CHART = {
  weeks: ['46', '47', '48', '49', '50', '51', '52', '01', '02'],
  highlightWeek: '51',
  // Índice 0,60–1,20 mapeado al alto del chart (y en px del viewBox original).
  yourLine: [139.33, 131.33, 123.33, 118, 99.33, 115.33, 107.33, 91.33, 78],
  bandTop: [115.33, 107.33, 99.33, 91.33, 78, 64.67, 59.33, 51.33, 40.67],
  bandBottom: [147.33, 139.33, 131.33, 123.33, 112.67, 102, 96.67, 88.67, 75.33],
  volHeights: [3.6, 9.1, 16.4, 25.5, 34.5, 40, 27.3, 16.4, 9.1],
  volLabel: '22% vol.',
  highlightLabel: '0,88',
  note: 'Semana 51 concentra el 22% de tu volumen y es tu mejor semana del período (0,88 vs. 1,00 de la industria). El deterioro de las semanas 01–02 pesa poco: entre ambas son el 14%.',
};

export interface VarietyRow extends PercRow {
  weeksKey: string[]; // chips de rango que la incluyen
}

export const VARIETY_WEEK_CHIPS = ['Todas las semanas', '45–48', '49–52', '01–04'];

export const VARIETIES: VarietyRow[] = [
  { name: 'Regina', sub: '12% vol · sem 01–03', iqrLeft: 45.45, iqrWidth: 29.09, median: 58.18, threshold: 54.55, you: 80, youLabel: '1,14', gap: '+0,12', weeksKey: ['01–04'] },
  { name: 'Lapins', sub: '18% vol · sem 52–02', iqrLeft: 40, iqrWidth: 29.09, median: 52.73, threshold: 54.55, you: 65.45, youLabel: '1,06', gap: '+0,07', weeksKey: ['49–52', '01–04'] },
  { name: 'Bing', sub: '6% vol · sem 50–52', iqrLeft: 32.73, iqrWidth: 27.27, median: 45.45, threshold: 54.55, you: 50.91, youLabel: '0,98', gap: '+0,03', weeksKey: ['49–52'] },
  { name: 'Kordia', sub: '9% vol · sem 01–02', iqrLeft: 36.36, iqrWidth: 27.27, median: 49.09, threshold: 54.55, you: 49.09, youLabel: '0,97', gap: '0,00', weeksKey: ['01–04'] },
  { name: 'Santina', sub: '31% vol · sem 49–52', iqrLeft: 34.55, iqrWidth: 27.27, median: 45.45, threshold: 54.55, you: 40, youLabel: '0,92', gap: '−0,03', weeksKey: ['49–52'] },
  { name: 'Sweetheart', sub: '8% vol · sem 02–04', iqrLeft: 30.91, iqrWidth: 32.73, median: 47.27, threshold: 54.55, you: 36.36, youLabel: '0,90', gap: '−0,06', weeksKey: ['01–04'] },
  { name: 'Royal Dawn', sub: '16% vol · sem 45–48', iqrLeft: 21.82, iqrWidth: 25.45, median: 32.73, threshold: 54.55, you: 14.55, youLabel: '0,78', gap: '−0,10', weeksKey: ['45–48'] },
];

export const VARIETIES_NOTE =
  'Regina y Lapins embarcan en enero y Royal Dawn en noviembre, así que parte de la brecha es calendario y no variedad. Usa el filtro de semanas para separarlos. Si en alguna variedad la industria completa aparece sobre 1,00, revisa el parámetro de vida útil antes que la operación.';

export const STRIPS = [
  { name: 'Embalaje → despacho packing', you: '1,8 d', median: '1,5 d', pill: 'p60', good: false, iqrLeft: 27.5, iqrWidth: 30, med: 37.5, youPos: 45, axis: ['0 d', '4 d'] },
  { name: 'Despacho packing → zarpe', you: '2,4 d', median: '3,1 d', pill: 'p31', good: true, iqrLeft: 36.67, iqrWidth: 40, med: 51.67, youPos: 40, axis: ['0 d', '6 d'] },
  { name: 'Zarpe → arribo', hint: 'depende de naviera', you: '24,5 d', median: '24,2 d', pill: 'p58', good: false, iqrLeft: 28, iqrWidth: 48, med: 44, youPos: 50, axis: ['22 d', '27 d'] },
  { name: 'Arribo → desconsolidación', you: '3,6 d', median: '4,4 d', pill: 'p33', good: true, iqrLeft: 40, iqrWidth: 36.25, med: 55, youPos: 45, axis: ['0 d', '8 d'] },
];

export const PLANTS = {
  yourMedian: '1,8 d',
  floorPct: 22.5, // mínimo técnico de preenfriado
  medianPct: 37.5,
  axis: ['0 d', '1 d', '2 d', '3 d', '4 d'],
  rows: [
    { name: 'Frigo Molina', tag: { label: 'servicio', warn: true }, sub: 'Maule · 11 exportadoras', vol: '12%', barPct: 17.5, color: STATUS_COLORS.critico, youLabel: '0,7', peak: '1,1', gap: '−0,8', gapKind: 'worst' as const },
    { name: 'Packing Curicó', tag: { label: 'propio' }, sub: 'Curicó · 38 líneas', vol: '42%', barPct: 30, color: STATUS_COLORS.holgado, youLabel: '1,2', peak: '1,9', gap: '—', gapKind: 'none' as const },
    { name: 'Packing Rengo', tag: { label: 'propio' }, sub: "O'Higgins · 24 líneas", vol: '27%', barPct: 40, color: '#45D483', youLabel: '1,6', peak: '2,8', gap: '—', gapKind: 'none' as const },
    { name: 'Agroservice S. Fernando', tag: { label: 'servicio', warn: true }, sub: "O'Higgins · 7 exportadoras", vol: '19%', barPct: 60, color: STATUS_COLORS.ajustado, youLabel: '2,4', peak: '3,6', gap: '+0,6', gapKind: 'bad' as const },
  ],
  note: 'Frigo Molina aparece como la planta más rápida, pero está bajo el mínimo de preenfriado: es una alerta de condición, no un logro. Y en la semana 51 todas las plantas casi duplican su tiempo, así que el ranking anual esconde el problema real, que es de capacidad en peak.',
};

export const CARRIERS = {
  yourMedian: '24,5 d',
  refPct: 35.71, // tu mediana ponderada
  axis: ['22 d', '24 d', '26 d', '28 d'],
  rows: [
    { name: 'Maersk', tag: { label: 'directo' }, sub: 'AC3 · Valparaíso–Shanghái', vol: '34%', iqrLeft: 17.14, iqrWidth: 20, median: 25.71, ataEta: '+0,4 d', ontime: '78%', kind: 'none' as const },
    { name: 'Hapag-Lloyd', tag: { label: 'directo' }, sub: 'CSW · San Antonio–Shanghái', vol: '22%', iqrLeft: 22.86, iqrWidth: 20, median: 30, ataEta: '+0,6 d', ontime: '71%', kind: 'none' as const },
    { name: 'COSCO', tag: { label: 'directo' }, sub: 'WSA · Valparaíso–Shanghái', vol: '28%', iqrLeft: 25.71, iqrWidth: 21.43, median: 34.29, ataEta: '+1,1 d', ontime: '64%', kind: 'bad' as const },
    { name: 'ONE', tag: { label: 'transbordo', warn: true }, sub: 'Andes · vía Busan', vol: '5%', iqrLeft: 34.29, iqrWidth: 41.43, median: 51.43, ataEta: '+2,2 d', ontime: '44%', kind: 'bad' as const },
    { name: 'MSC', tag: { label: 'transbordo', warn: true }, sub: 'Santana · vía Manzanillo', vol: '11%', iqrLeft: 41.43, iqrWidth: 45.71, median: 60, ataEta: '+2,8 d', ontime: '38%', kind: 'worst' as const },
  ],
  note: 'Los dos servicios con transbordo son el 16% de tu volumen y explican 1,9 de los 2,4 días de dispersión del tramo. El atraso de MSC Santana movió 9% de tu volumen de ajustado a vencido: recalculado con el ETA prometido, tu índice del tramo habría sido 0,89 en vez de 0,94.',
};

export const ARRIVAL_CHART = {
  weeks: ['51', '52', '01', '02', '03', '04', '05', '06', '07', '08', '09'],
  // Desviación vs. mediana industria (px desde y=110 del viewBox; negativo = mejor).
  bars: [
    { h: 18.67, up: false }, { h: 28, up: false }, { h: 14, up: false },
    { h: 9.33, up: true }, { h: 37.33, up: false }, { h: 51.33, up: false },
    { h: 23.33, up: false }, { h: 18.67, up: true }, { h: 42, up: true },
    { h: 65.33, up: true }, { h: 51.33, up: true },
  ],
  you: ['3,4', '3,5', '4,3', '5,4', '3,6', '2,9', '3,2', '3,9', '4,3', '4,7', '4,3'],
  industry: ['3,8', '4,1', '4,6', '5,2', '4,4', '4,0', '3,7', '3,5', '3,4', '3,3', '3,2'],
  youBest: 5, // índice de la mejor semana (2,9)
  youWorst: 3, // índice de la peor (5,4)
  cnyIndex: 9, // Año Nuevo chino en semana 08
  industryArrivals: [216.7, 204.9, 191.6, 185, 194.6, 201.2, 207.9, 214.5, 219.7, 223.4, 227],
  arrivalsLabel: '610 cont. industria',
  note: 'Las dos lecturas no coinciden, y ahí está el valor: en la semana 02 estás casi en línea con el mercado (+0,2 d) pero es tu peor semana en absoluto (5,4 d), porque el peak de arribos satura a todos. En la semana 08 eres el peor del período en desviación (+1,4 d) y aun así vendes más rápido que en la 02. La desviación mide tu gestión; el absoluto mide cuánta vida útil se consume de verdad, y es el que entra al índice de frescura.',
};

export const RECEIVERS = {
  industryMedian: '3,4 d',
  medianPct: 48.57,
  axis: ['0 d', '2 d', '4 d', '6 d'],
  rows: [
    { name: 'RCB-014', sub: 'Shanghái · 26 cont.', vol: '38%', barPct: 34.29, color: STATUS_COLORS.holgado, youLabel: '2,4', vessel: '+0,5', total: '2,9 d', kind: 'none' as const },
    { name: 'RCB-027', sub: 'Shanghái · 17 cont.', vol: '24%', barPct: 44.29, color: '#45D483', youLabel: '3,1', vessel: '+0,8', total: '3,9 d', kind: 'none' as const },
    { name: 'RCB-005', sub: 'Guangzhou · 13 cont.', vol: '18%', barPct: 51.43, color: STATUS_COLORS.ajustado, youLabel: '3,6', vessel: '+1,2', total: '4,8 d', kind: 'bad' as const },
    { name: 'RCB-019', sub: 'Guangzhou · 6 cont.', vol: '8%', barPct: 77.14, color: STATUS_COLORS.leve, youLabel: '5,4', vessel: '+1,1', total: '6,5 d', kind: 'bad' as const },
    { name: 'RCB-031', sub: 'Shanghái · 9 cont.', vol: '12%', barPct: 70, color: STATUS_COLORS.leve, youLabel: '4,9', vessel: '+2,6', total: '7,5 d', kind: 'worst' as const },
  ],
  note: 'RCB-031 es tu peor brecha total (7,5 d), pero solo 4,9 son suyos: el resto lo puso la nave. RCB-019 desconsolida más lento (5,4 d) con naves puntuales, así que ahí el problema sí es del recibidor. Mover el 12% de RCB-031 a RCB-014 recuperaría 2,5 días de vida útil en ese volumen.',
};

export const METHODOLOGY = [
  { k: 'Base de cálculo', v: 'Edad desde fecha de embalaje, caja a caja. Cosecha → embalaje se reporta aparte por cobertura parcial.' },
  { k: 'Ponderación', v: 'Por kilos netos, no por cajas ni por lote.' },
  { k: 'Fecha de cierre', v: 'Devolución de contenedor como proxy de desconsolidación. Venta real se guarda en paralelo.' },
  { k: 'Vida útil', v: 'Tabla por variedad, versión 3, vigente desde 01-nov-2025.' },
  { k: 'Confidencialidad', v: 'Mínimo 5 exportadoras por cohorte. Tu volumen no entra en tu propio benchmark.' },
  { k: 'Atípicos', v: 'Se marcan devoluciones sobre P95 del puerto en la semana. 4 contenedores excluidos.' },
];
